import { useState } from "react";
import { Search, Package, CheckCircle, Clock, Truck, XCircle, AlertCircle, Loader2, MessageCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// FarmSmartKids business WhatsApp (Barbados). Digits-only for wa.me deep links.
const BUSINESS_WA_NUMBER = "12462396575";

const STATUS_META = {
  pending_payment: { label: "Pending Payment", color: "#E9C46A", icon: Clock, message: "We're waiting for your PayPal payment confirmation." },
  paid: { label: "Paid", color: "#2A9D8F", icon: CheckCircle, message: "Payment received! We'll prepare your order soon." },
  preparing: { label: "Preparing", color: "#457B9D", icon: Package, message: "Your order is being prepared." },
  shipped: { label: "Shipped / Out for delivery", color: "#457B9D", icon: Truck, message: "Your order is on the way!" },
  delivered: { label: "Delivered", color: "#2A9D8F", icon: CheckCircle, message: "Delivered. Hope you love it 🐄📚" },
  cancelled: { label: "Cancelled", color: "#E63946", icon: XCircle, message: "This order was cancelled. Email info@farmsmartkids.com if that's unexpected." }
};

const DELIVERY_LABELS = {
  pickup: "Barbados Pickup",
  delivery: "Barbados Delivery",
  rural: "Barbados Rural Delivery"
};

const OrderStatusLookup = () => {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  const handleLookup = async (e) => {
    e.preventDefault();
    setError(null);
    setOrder(null);
    if (!orderNumber.trim() || !email.trim()) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ email: email.trim() });
      const { data } = await axios.get(`${API}/api/orders/${orderNumber.trim().toUpperCase()}?${params}`);
      setOrder(data);
    } catch (err) {
      setError(
        err.response?.status === 404
          ? "We couldn't find that order. Double-check the order number and the email you used at checkout."
          : "Something went wrong. Please try again in a moment."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setOrder(null);
    setError(null);
    setOrderNumber("");
    setEmail("");
  };

  const meta = order ? (STATUS_META[order.status] || STATUS_META.pending_payment) : null;
  const StatusIcon = meta?.icon;

  return (
    <div className="bg-white/5 border-2 border-white/15 rounded-2xl p-6 mb-10" data-testid="order-status-lookup">
      <h3 className="text-xl font-bold mb-1 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
        <Search className="w-6 h-6 text-[#E9C46A]" />
        Track your order
      </h3>
      <p className="text-white/70 text-sm font-medium mb-4">
        Enter the order number from your confirmation page along with the email you used.
      </p>

      {!order && (
        <form onSubmit={handleLookup} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
          <input
            type="text"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            placeholder="FSK-YYYYMMDD-XXXXXX"
            className="px-4 py-3 rounded-xl bg-white text-[#1D3557] placeholder-[#1D3557]/40 font-bold font-mono text-sm focus:outline-none focus:ring-3 focus:ring-[#E9C46A]"
            data-testid="order-number-input"
            required
            aria-label="Order number"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="px-4 py-3 rounded-xl bg-white text-[#1D3557] placeholder-[#1D3557]/40 font-medium text-sm focus:outline-none focus:ring-3 focus:ring-[#E9C46A]"
            data-testid="order-email-input"
            required
            aria-label="Email used at checkout"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 rounded-xl bg-[#E63946] hover:bg-[#E9C46A] hover:text-[#1D3557] text-white font-bold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            data-testid="lookup-order-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
            {loading ? "Looking…" : "Look up"}
          </button>
        </form>
      )}

      {error && !order && (
        <div className="mt-4 p-3 rounded-xl bg-[#E63946]/20 border-2 border-[#E63946] flex items-start gap-3" data-testid="lookup-error">
          <AlertCircle className="w-5 h-5 text-[#E9C46A] flex-shrink-0 mt-0.5" />
          <p className="text-white font-medium text-sm">{error}</p>
        </div>
      )}

      {order && meta && (
        <div className="mt-2 bg-white text-[#1D3557] rounded-2xl p-5" data-testid="order-status-result">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <p className="text-xs uppercase font-bold tracking-wider text-[#1D3557]/60">Order</p>
              <p className="font-mono font-bold text-lg" data-testid="result-order-number">{order.order_number}</p>
            </div>
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white font-bold text-sm"
              style={{ backgroundColor: meta.color }}
              data-testid="result-status-badge"
            >
              <StatusIcon className="w-4 h-4" />
              {meta.label}
            </span>
          </div>

          <p className="text-[#1D3557]/80 font-medium mb-4">{meta.message}</p>

          {order.status === "pending_payment" && (() => {
            const paidMessage = `Hi FarmSmartKids! 👋\n\nI've paid for my order:\nOrder #${order.order_number}\nTotal: $${order.total.toFixed(2)} ${order.currency}\n\nPlease confirm when you can. Thanks! 🐄`;
            const paidLink = `https://wa.me/${BUSINESS_WA_NUMBER}?text=${encodeURIComponent(paidMessage)}`;
            return (
              <a
                href={paidLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold px-5 py-3 rounded-xl border-2 border-[#1D3557] shadow-[3px_3px_0px_0px_rgba(29,53,87,1)] hover:shadow-[5px_5px_0px_0px_rgba(29,53,87,1)] hover:-translate-y-0.5 transition-all text-sm mb-4"
                data-testid="ive-paid-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
                I&apos;ve paid! Notify us on WhatsApp
              </a>
            );
          })()}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="bg-[#1D3557]/5 rounded-lg p-3">
              <p className="text-xs uppercase font-bold tracking-wider text-[#1D3557]/60">Items</p>
              <p className="font-bold">{order.quantity} × book</p>
            </div>
            <div className="bg-[#1D3557]/5 rounded-lg p-3">
              <p className="text-xs uppercase font-bold tracking-wider text-[#1D3557]/60">Total</p>
              <p className="font-bold">${order.total.toFixed(2)} {order.currency}</p>
            </div>
            <div className="bg-[#1D3557]/5 rounded-lg p-3">
              <p className="text-xs uppercase font-bold tracking-wider text-[#1D3557]/60">Method</p>
              <p className="font-bold">{DELIVERY_LABELS[order.delivery_method] || "Standard"}</p>
            </div>
            <div className="bg-[#1D3557]/5 rounded-lg p-3">
              <p className="text-xs uppercase font-bold tracking-wider text-[#1D3557]/60">Placed</p>
              <p className="font-bold">{new Date(order.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <a
              href={`mailto:info@farmsmartkids.com?subject=${encodeURIComponent(`Question about order ${order.order_number}`)}`}
              className="text-sm font-bold text-[#E63946] hover:underline"
              data-testid="result-email-link"
            >
              Email us about this order
            </a>
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 rounded-xl bg-[#1D3557] text-white font-bold text-sm hover:bg-[#2A9D8F] transition-colors"
              data-testid="lookup-reset-btn"
            >
              Look up another
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderStatusLookup;
